import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import logo from '../../images/logo.jpg';
import { useAppNavigation } from '../../hooks';
import { ROUTES } from '../../constants';
import LanguageSwitcher from '../LanguageSwitcher';
import './Navigation.css';

/**
 * Main navigation component
 * Fixed header with logo, links, and CTA buttons
 * Language defaults to system setting, but can be manually switched
 */
const Navigation: React.FC = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const { goToMenu, goToGallery, goToFAQ } = useAppNavigation();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const toggleMobileMenu = () => {
		setMobileMenuOpen(!mobileMenuOpen);
	};

	const closeMobileMenu = () => {
		setMobileMenuOpen(false);
	};

	return (
		<nav className="navbar" role="navigation" aria-label="Main navigation">
			<div className="nav-container">
				<div className="nav-logo">
					<Link to={ROUTES.HOME} aria-label="Family Friends Hibachi Home">
						<img src={logo} alt="Family Friends Hibachi Logo" className="logo-image" />
					</Link>
				</div>

				{/* Mobile menu toggle */}
				<button
					className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
					onClick={toggleMobileMenu}
					aria-label="Toggle menu"
					aria-expanded={mobileMenuOpen}
				>
					<span></span>
					<span></span>
					<span></span>
				</button>

				<div className={`nav-links ${mobileMenuOpen ? 'open' : ''}`}>
					<Link
						to={ROUTES.HOME}
						className={location.pathname === '/' ? 'active' : ''}
						onClick={closeMobileMenu}
					>
						{t('nav.home')}
					</Link>
					<Link to={`${ROUTES.BOOK_NOW}#california`} onClick={closeMobileMenu}>
						{t('nav.california')}
					</Link>
					<Link to={`${ROUTES.BOOK_NOW}#texas`} onClick={closeMobileMenu}>
						{t('nav.texas')}
					</Link>
					<Link to={`${ROUTES.BOOK_NOW}#florida`} onClick={closeMobileMenu}>
						{t('nav.florida')}
					</Link>
					<button
						onClick={() => {
							goToMenu();
							closeMobileMenu();
						}}
						className="nav-link-btn"
						type="button"
					>
						{t('nav.menu')}
					</button>
					<button
						onClick={() => {
							goToGallery();
							closeMobileMenu();
						}}
						className="nav-link-btn"
						type="button"
					>
						{t('gallery.title')}
					</button>
					<button
						onClick={() => {
							goToFAQ();
							closeMobileMenu();
						}}
						className="nav-link-btn"
						type="button"
					>
						{t('nav.faq')}
					</button>

					{/* Mobile language switcher */}
					<div className="mobile-language-switcher">
						<LanguageSwitcher />
					</div>
				</div>

				<div className="nav-buttons">
					<LanguageSwitcher />
					<Link to={ROUTES.FREE_ESTIMATE} className="btn-secondary">
						{t('nav.freeEstimate')}
					</Link>
					<Link to={ROUTES.BOOK_NOW} className="btn-primary">
						{t('nav.bookNow')}
					</Link>
				</div>
			</div>

			{/* Mobile menu overlay */}
			{mobileMenuOpen && <div className="mobile-menu-overlay" onClick={closeMobileMenu}></div>}
		</nav>
	);
};

export default Navigation;
